/**
 * Granular, module-scoped permission model.
 *
 * A role's permissions are stored as a nested object:
 *   { [module]: { [action]: boolean } }
 * mirroring the backend `roles.permissions` shape exactly.
 *
 * Components/sidebar/routes gate on (module, action) pairs via the
 * usePermissions hook and utils/permissions helpers.
 */

export const MODULES = {
  DASHBOARD: 'dashboard',
  STAGE1: 'stage1',
  STAGE2: 'stage2',
  BRIEFING: 'briefing',
  STORAGE1: 'storage1',
  STORAGE2: 'storage2',
  OCR_STORAGE: 'ocr_storage',
  NO_STORAGE: 'no_storage',
  SEARCH: 'search',
  SETTINGS: 'settings',        // sidebar group only
  ROLES: 'roles',              // settings sub-module
  USERS: 'users',              // settings sub-module
  NEWSPAPERS: 'newspapers',    // settings sub-module
  SYSTEM_CONFIG: 'system_config', // settings sub-module
  ACTIVITY_LOGS: 'activity_logs', // settings sub-module
};

export const ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  APPROVE: 'approve',
  REJECT: 'reject',
  BULK_APPROVE: 'bulk_approve',
  BULK_REJECT: 'bulk_reject',
  MANUAL_UPLOAD: 'manual_upload',
  MANUAL_NEWSPAPER_UPLOAD: 'manual_newspaper_upload',
  MANUAL_ARTICLES_UPLOAD: 'manual_articles_upload',
  MANUAL_TELEGRAM_RUN: 'manual_telegram_run',
  ROUTE: 'route',
  BULK_ROUTE: 'bulk_route',
  DISPATCH: 'dispatch',
  CONFIGURE_SCHEDULE: 'configure_schedule',
};

/**
 * Declares which actions exist per module. Drives the Role permission-matrix
 * editor and validation. Order here is the display order.
 */
export const PERMISSION_SCHEMA = [
  { module: MODULES.DASHBOARD, label: 'Dashboard', actions: [ACTIONS.VIEW] },
  {
    module: MODULES.STAGE1,
    label: 'Stage 1 · Article Feed',
    actions: [
      ACTIONS.VIEW,
      ACTIONS.EDIT,
      ACTIONS.APPROVE,
      ACTIONS.REJECT,
      ACTIONS.BULK_APPROVE,
      ACTIONS.BULK_REJECT,
      ACTIONS.MANUAL_NEWSPAPER_UPLOAD,
      ACTIONS.MANUAL_ARTICLES_UPLOAD,
      ACTIONS.MANUAL_TELEGRAM_RUN,
    ],
  },
  { 
    module: MODULES.STAGE2, 
    label: 'Stage 2 · Routing', 
    actions: [
      ACTIONS.VIEW, 
      ACTIONS.ROUTE,
      ACTIONS.BULK_ROUTE,
      ACTIONS.MANUAL_ARTICLES_UPLOAD
    ] 
  },
  {
    module: MODULES.BRIEFING,
    label: 'Sahebji Briefing',
    actions: [
      ACTIONS.VIEW, 
      ACTIONS.ROUTE, 
      ACTIONS.CONFIGURE_SCHEDULE,
      ACTIONS.BULK_ROUTE
    ],
  },
  { module: MODULES.STORAGE1, label: 'Storage 1 · TRR', actions: [ACTIONS.VIEW, ACTIONS.DISPATCH] },
  { module: MODULES.STORAGE2, label: 'Storage 2 · Archive', actions: [ACTIONS.VIEW] },
  { module: MODULES.OCR_STORAGE, label: 'OCR Storage', actions: [ACTIONS.VIEW] },
  { module: MODULES.NO_STORAGE, label: 'No Storage', actions: [ACTIONS.VIEW] },
  { module: MODULES.SEARCH, label: 'Search', actions: [ACTIONS.VIEW] },
  { module: MODULES.SETTINGS, label: 'Settings', actions: [ACTIONS.VIEW] },
  { module: MODULES.ROLES, label: 'Roles', actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE] },
  { module: MODULES.USERS, label: 'Users', actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE] },
  {
    module: MODULES.NEWSPAPERS,
    label: 'Newspaper Master',
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.DELETE],
  },
  { module: MODULES.SYSTEM_CONFIG, label: 'System Configuration', actions: [ACTIONS.VIEW, ACTIONS.EDIT] },
  { module: MODULES.ACTIVITY_LOGS, label: 'Activity Logs', actions: [ACTIONS.VIEW] },
];

export const ACTION_LABELS = {
  [ACTIONS.VIEW]: 'View',
  [ACTIONS.CREATE]: 'Create',
  [ACTIONS.EDIT]: 'Edit',
  [ACTIONS.DELETE]: 'Delete',
  [ACTIONS.APPROVE]: 'Approve',
  [ACTIONS.REJECT]: 'Reject',
  [ACTIONS.BULK_APPROVE]: 'Bulk approve',
  [ACTIONS.BULK_REJECT]: 'Bulk reject',
  [ACTIONS.MANUAL_UPLOAD]: 'Manual upload',
  [ACTIONS.MANUAL_NEWSPAPER_UPLOAD]: 'Manual newspaper upload',
  [ACTIONS.MANUAL_ARTICLES_UPLOAD]: 'Manual articles upload',
  [ACTIONS.MANUAL_TELEGRAM_RUN]: 'Manual telegram run',
  [ACTIONS.ROUTE]: 'Route',
  [ACTIONS.BULK_ROUTE]: 'Bulk route',
  [ACTIONS.DISPATCH]: 'Dispatch',
  [ACTIONS.CONFIGURE_SCHEDULE]: 'Configure schedule',
};

/** Build an empty permission object (all false) from the schema. */
export const buildEmptyPermissions = () =>
  PERMISSION_SCHEMA.reduce((acc, { module, actions }) => {
    acc[module] = actions.reduce((a, action) => ({ ...a, [action]: false }), {});
    return acc;
  }, {});

/** Build a full-access permission object (all true) from the schema. */
export const buildFullPermissions = () =>
  PERMISSION_SCHEMA.reduce((acc, { module, actions }) => {
    acc[module] = actions.reduce((a, action) => ({ ...a, [action]: true }), {});
    return acc;
  }, {});

export default PERMISSION_SCHEMA;
